import { HttpParams } from '@angular/common/http';

import DOMPurify, { Config } from 'dompurify';

import { VideoEmbedInfo } from '@shared/models/video-embed.model';
import { pickBy } from './object.utilities';

export function capitalize(text: string) {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

export function emailToName(email: string) {
  const name = email.split('@')[0].split('.')[0];
  return capitalize(name);
}

export function slugify(text: string) {
  return text.toLowerCase().replace(/\W+/g, '-');
}

export function queryParamsString(params: any) {
  const nonEmptyParams = pickBy(params, (value) => !!value);

  return Object.keys(nonEmptyParams).length > 0
    ? '?' +
        Object.entries(nonEmptyParams)
          .map(([k, v]) => `${k}=${v}`)
          .join('&')
    : '';
}

export function queryParamsObject(value: string): object {
  const searchParams = new URLSearchParams(value);
  return Object.fromEntries(searchParams as any);
}

export function encodeState<T>(state: T): string | null {
  try {
    return btoa(JSON.stringify(state));
  } catch {
    return null;
  }
}

export function decodeState<T>(state: string): T | null {
  try {
    return JSON.parse(atob(state)) as T;
  } catch {
    return null;
  }
}

export function searchQueryMatch(target: string, search: string): boolean {
  return searchQueryData(target, search).length > 0;
}

export function searchQueryData(target: string, search: string): { part: string; idx: number }[] {
  if (!search || !target) {
    return [];
  }

  search = search.toLocaleLowerCase();
  target = target.toLocaleLowerCase();

  const searches = search.split(' ').filter((part) => !!part);

  const matchData = searches
    .map((part) => {
      const indices: number[] = [];
      let i = -1;

      while ((i = target.indexOf(part, i + 1)) !== -1) {
        indices.push(i);
      }

      return indices;
    })
    .filter((matches) => !!matches.length);

  if (searches.length > matchData.length) {
    return [];
  }

  const matchIndices = matchData.reduce((matches, match, idx) => {
    const prevMatches = matches.slice(0, idx);

    const matchedIdx = prevMatches.length
      ? match.findIndex((matchIdx) =>
          prevMatches.every(
            (prevMatch, prevIdx) =>
              matchIdx + searches[idx].length <= prevMatch || matchIdx >= prevMatch + searches[prevIdx].length,
          ),
        )
      : 0;

    if (matchedIdx > -1) {
      matches.push(match[matchedIdx]);
    }

    return matches;
  }, []);

  if (searches.length > matchIndices.length) {
    return [];
  }

  return matchIndices.map((idx, i) => ({ part: searches[i], idx })).sort((a, b) => a.idx - b.idx);
}

export function isImageFile(file: File | string): boolean {
  file = file instanceof File ? file.name : file;

  const parts = file.split('.');

  return (
    parts.length > 1 && ['png', 'jpeg', 'jpg', 'gif', 'svg', 'webp', 'bmp', 'tiff'].includes(parts.pop().toLowerCase())
  );
}

export function getYouTubeVideoInfo(url: string): VideoEmbedInfo | null {
  const idRegExp = new RegExp('^[A-Za-z\\d-_]{11}$');

  if (idRegExp.test(url)) {
    return { videoId: url, provider: 'youtube' };
  }

  const regExp = new RegExp('(?:\\/|\\bv=\\b)([A-Za-z\\d-_]{11}\\b)');

  const matches = url.match(regExp);

  const videoId = matches?.[1];

  if (videoId) {
    const params = new URLSearchParams(url.split('?')[1]);
    const startTime = params.get('t') || params.get('start');
    const endTime = params.get('end') || params.get('stop');

    return {
      videoId,
      startTime: parseInt(startTime, 10) || 0,
      endTime: parseInt(endTime, 10) || 0,
      provider: 'youtube',
    };
  }

  return null;
}

export function getYouTubeEmbedUrl(info: VideoEmbedInfo): string {
  const url = `https://www.youtube.com/embed/${info.videoId}`;

  let params = new HttpParams();

  if (info.startTime) {
    params = params.set('start', info.startTime.toString());
  }

  if (info.endTime) {
    params = params.set('end', info.endTime.toString());
  }

  const query = params.toString();

  return url + (query ? `?${query}` : '');
}

export function addProtocolToUrl(url: string, protocol: 'http' | 'https' = 'https'): string {
  if (!url || url.startsWith('http:') || url.startsWith('https:')) {
    return url;
  }

  return `${protocol}:${url.startsWith('//') ? '' : '//'}${url}`;
}

export function trimStart(str: string, char: string = 's'): string {
  return str.replace(new RegExp(`^${char}*`), '');
}

export function randomString(length = 16): string {
  let result = '';

  const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';

  for (let i = 0; i < length; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }

  return result;
}

export function ellipsis(str: string, maxLength: number, ellipsisChar = '…'): string {
  if (!str || str.length <= maxLength) {
    return str;
  }

  return `${str.substr(0, 99)}${ellipsisChar}`;
}

export function sanitizeHtml(str: string) {
  if (document) {
    const el = document.createElement('div');
    el.innerHTML = str;

    return el.innerText;
  } else {
    return str;
  }
}

export function assertString(target: unknown): string {
  return target?.toString() || '';
}

export function toLines(str: string): string[] {
  const lines = (str || '')
    .split(/\r\n|\r|\n/g)
    .map((c) => c.trim())
    .filter((c) => !!c);

  return lines;
}

export function isMultiline(str: string): boolean {
  return toLines(str).length > 1;
}

export function getStringValuesFromObject(obj: object, result?: string[]): string[] {
  result ||= [];

  if (obj != null) {
    if (typeof obj === 'string') {
      result.push(obj);
    } else if (Array.isArray(obj)) {
      const arrayResults = obj.map((item) => getStringValuesFromObject(item)).reduce((a, b) => a.concat(b));
      result.push(...arrayResults);
    } else if (typeof obj === 'object') {
      const objResults = Object.values(obj)
        .map((value) => getStringValuesFromObject(value))
        .reduce((a, b) => a.concat(b));
      result.push(...objResults);
    }
  }

  return result;
}

export function isFirebaseKey(value: string) {
  return value?.length === 20 || value?.startsWith('+');
}

export function getPlainText(str: string | undefined): string | undefined {
  if (!str) {
    return '';
  }

  if (str.includes('<')) {
    str = str.replace(/<\/?("[^"]*"|'[^']*'|[^>])*(>|$)/g, '');
  }

  return str;
}

export function AIModelDependentMessage(result: any): string {
  const modelKnown = result?.usedModelKey || '';
  const gpt = result?.[0]?.message?.content;
  const claude = result?.content?.[0]?.text;
  const gemini = result?.candidates?.[0]?.content?.parts?.[0]?.text;

  if (modelKnown.includes('claude')) {
    return claude || '';
  }

  if (modelKnown.includes('gemini')) {
    return gemini || '';
  }

  return gpt || claude || gemini || '';
}

export function AIModelDependentFunctionCallContent(result: any): string | undefined {
  const modelKnown = result?.usedModelKey || '';

  // GPT format: result[0].message.function_call.arguments or result[0].message.tool_calls[0].function.arguments
  const gptFunctionCall = result?.[0]?.message?.function_call?.arguments;
  const gptToolCall = result?.[0]?.message?.tool_calls?.[0]?.function?.arguments;

  // Claude format: find content item with type 'tool_use'
  const claudeToolUse = result?.content?.find((item: any) => item?.type === 'tool_use');
  const claudeInput = claudeToolUse?.input;

  // Gemini format
  const geminiPart = result?.candidates?.[0]?.content?.parts?.find((p: any) => p.functionCall);
  const geminiArgs = geminiPart?.functionCall?.args;

  if (modelKnown.includes('claude')) {
    return typeof claudeInput === 'string' ? claudeInput : JSON.stringify(claudeInput);
  }

  if (modelKnown.includes('gemini')) {
    return geminiArgs ? JSON.stringify(geminiArgs) : undefined;
  }

  return (
    gptFunctionCall ||
    gptToolCall ||
    (claudeInput ? JSON.stringify(claudeInput) : undefined) ||
    (geminiArgs ? JSON.stringify(geminiArgs) : undefined)
  );
}

export interface AIGeneratedImage {
  bytesBase64Encoded: string;
  mimeType: string;
}

export function AIModelDependentImage(result: any): AIGeneratedImage | undefined {
  const modelKnown: string = result?.usedModelKey || '';

  // Imagen format
  const imagenPrediction = result?.predictions?.[0];
  const imagen: AIGeneratedImage | undefined = imagenPrediction?.bytesBase64Encoded
    ? {
        bytesBase64Encoded: imagenPrediction.bytesBase64Encoded,
        mimeType: imagenPrediction.mimeType || 'image/png',
      }
    : undefined;

  // GPT Image format
  const gptImageItem = result?.data?.[0];
  const gptImageData = gptImageItem?.b64_json || gptImageItem?.b64__json;
  const gptImage: AIGeneratedImage | undefined = gptImageData
    ? {
        bytesBase64Encoded: gptImageData,
        mimeType: gptImageItem.mime_type || (result?.output_format ? `image/${result.output_format}` : 'image/png'),
      }
    : undefined;

  // Gemini format
  const geminiPart = result?.candidates?.[0]?.content?.parts?.find(
    (part: any) => part?.inlineData?.data || part?.inline_data?.data,
  );
  const geminiInline = geminiPart?.inlineData || geminiPart?.inline_data;
  const gemini: AIGeneratedImage | undefined = geminiInline?.data
    ? {
        bytesBase64Encoded: geminiInline.data,
        mimeType: geminiInline.mimeType || geminiInline.mime_type || 'image/png',
      }
    : undefined;

  if (modelKnown.includes('gpt-image')) {
    return gptImage;
  }

  if (modelKnown.includes('imagen')) {
    return imagen;
  }

  if (modelKnown.includes('gemini')) {
    return gemini;
  }

  return imagen || gptImage || gemini;
}

export function GeminiGroundedResponse(result: any, showSources?: boolean): string {
  const text = result?.candidates?.[0]?.content?.parts?.[0]?.text || '';

  if (!showSources || !text) {
    return text;
  }

  const candidate = result?.candidates?.[0];
  const supports: any[] = candidate?.groundingMetadata?.groundingSupports ?? [];
  const chunks: any[] = candidate?.groundingMetadata?.groundingChunks ?? [];

  if (!supports?.length || !chunks?.length) {
    return text;
  }

  const getSiteForChunk = (index: number): string | undefined => {
    const web = chunks?.[index]?.web;

    if (!web) {
      return undefined;
    } else if (web.domain) {
      return String(web.domain);
    }

    const uri: string | undefined = web.uri ?? web.url;

    try {
      return uri ? new URL(uri).hostname.replace(/^www\./, '') : undefined;
    } catch {
      return undefined;
    }
  };

  const injections: { start: number; end: number; suffix: string }[] = [];

  for (const support of supports) {
    const segment = support?.segment;
    const chunkIndices: number[] = support?.groundingChunkIndices ?? [];

    if (
      !segment ||
      !Number.isFinite(segment.startIndex) ||
      !Number.isFinite(segment.endIndex) ||
      !chunkIndices.length
    ) {
      continue;
    }

    let site: string | undefined;

    for (const index of chunkIndices) {
      site = getSiteForChunk(index);

      if (site) {
        break;
      }
    }

    if (!site) {
      continue;
    }

    injections.push({
      start: segment.startIndex,
      end: segment.endIndex,
      suffix: ` (source: ${site})`,
    });
  }

  if (!injections.length) {
    return text;
  }

  injections.sort((a, b) => b.end - a.end);

  let output = text;

  for (const injection of injections) {
    const start = Math.max(0, Math.min(output.length, injection.start));
    const end = Math.max(start, Math.min(output.length, injection.end));

    output = output.slice(0, end) + injection.suffix + output.slice(end);
  }

  return output;
}

export function ClaudeWebSearchResponse(result: any): string {
  if (!result?.content) {
    return '';
  }

  const content = result.content;
  let markdown = '';
  const citationMap = new Map<string, number>();
  let citationCounter = 1;

  // Process content blocks
  for (const block of content) {
    if (block.type === 'text') {
      let text = block.text || '';

      // Process citations if they exist
      if (block.citations && Array.isArray(block.citations)) {
        for (const citation of block.citations) {
          if (citation.type === 'web_search_result_location') {
            const url = citation.url;
            const citedText = citation.cited_text || '';

            // Get or create citation number
            let citationNum: number;
            if (citationMap.has(url)) {
              citationNum = citationMap.get(url)!;
            } else {
              citationNum = citationCounter++;
              citationMap.set(url, citationNum);
            }

            // Add citation marker to the text
            text = text.replaceAll(citedText, `${citedText}[${citationNum}]`);
          }
        }
      }

      markdown += text;
    }
  }

  // Add citations section at the end if there are any citations
  if (citationMap.size > 0) {
    markdown += '\n\n---\n\n### Sources\n\n';

    // Sort citations by number
    const sortedCitations = Array.from(citationMap.entries()).sort((a, b) => a[1] - b[1]);

    for (const [url, num] of sortedCitations) {
      // Find the title for this URL from the web_search_tool_result
      let title = url;
      const toolResult = content.find((block) => block.type === 'web_search_tool_result');

      if (toolResult?.content) {
        const searchResult = toolResult.content.find((item) => item.url === url);
        if (searchResult?.title) {
          title = searchResult.title;
        }
      }

      markdown += `${num}. [${title}](${url})\n`;
    }
  }

  return markdown.trim();
}

export function AIModelDependentFunctionResult(result: any): any {
  const modelKnown = result?.usedModelKey || '';
  const gpt =
    result?.[0]?.message?.content?.function_call?.arguments ||
    result?.[0]?.message?.content?.tool_calls?.[0]?.function?.arguments;
  const claude = result?.content?.[1]?.input;
  const parser = (result) => {
    if (typeof result === 'string') {
      try {
        return JSON.parse(result);
      } catch (e) {
        console.log(e);
      }
    } else {
      return result;
    }
  };

  return parser(modelKnown.includes('claude') ? claude : gpt || claude || {});
}

export function AIModelDependentToolCall(result: any): any {
  const modelKnown = result?.usedModelKey || '';
  // TODO: Check if this is correct for GPT
  const gpt =
    result?.[0]?.message?.content?.function_call?.arguments ||
    result?.[0]?.message?.content?.tool_calls?.[0]?.function?.arguments ||
    [];
  const claude = result?.content?.filter((item) => item?.type === 'tool_use') || [];

  return modelKnown.includes('claude') ? (claude?.length ? claude : []) : gpt || claude || [];
}

/**
 * Normalizes an agentic AI response (with tool_use / function calls) into a
 * consistent format regardless of the underlying model (Claude, Gemini, GPT).
 *
 * The normalized output uses Claude's structure as the canonical format:
 * - `content`: array of `{ type: 'text', text }` and `{ type: 'tool_use', id, name, input }` blocks
 * - `usage`: `{ input_tokens, output_tokens }`
 *
 * This allows all agent consumers to use a single code path for processing
 * tool calls, text responses, and token tracking.
 */
export function AIModelDependentAgentResponse(result: any): {
  content: any[];
  usage: {
    input_tokens: number;
    output_tokens: number;
    cache_read_input_tokens?: number;
    cache_creation_input_tokens?: number;
    cache_creation?: { ephemeral_5m_input_tokens?: number; ephemeral_1h_input_tokens?: number };
  };
  usedModelKey: string;
} {
  const modelKnown = result?.usedModelKey || '';

  // Normalize usage across all model formats
  let inputTokens = 0;
  let outputTokens = 0;

  if (result?.usage) {
    // Claude / OpenAI format
    inputTokens = result.usage.input_tokens || 0;
    outputTokens = result.usage.output_tokens || 0;
  } else if (result?.usageMetadata) {
    // Gemini format
    inputTokens = (result.usageMetadata.promptTokenCount || 0) + (result.usageMetadata.toolUsePromptTokenCount || 0);
    outputTokens = (result.usageMetadata.candidatesTokenCount || 0) + (result.usageMetadata.thoughtsTokenCount || 0);
  }

  const usage = {
    input_tokens: inputTokens,
    output_tokens: outputTokens,
    // Claude reports its prompt cache use apart from input_tokens, and it is priced apart
    ...(result?.usage?.cache_read_input_tokens != null
      ? { cache_read_input_tokens: result.usage.cache_read_input_tokens }
      : {}),
    ...(result?.usage?.cache_creation_input_tokens != null
      ? { cache_creation_input_tokens: result.usage.cache_creation_input_tokens }
      : {}),
    ...(result?.usage?.cache_creation ? { cache_creation: result.usage.cache_creation } : {}),
  };

  // Claude format: result.content is already in the correct format
  if (modelKnown.includes('claude')) {
    return { content: result?.content || [], usage, usedModelKey: modelKnown };
  }

  // Gemini format: result.candidates[0].content.parts[]
  if (modelKnown.includes('gemini') || modelKnown.includes('google')) {
    const parts = result?.candidates?.[0]?.content?.parts || [];
    const content: any[] = [];
    let callIndex = 0;

    for (const part of parts) {
      if (part.functionCall) {
        content.push({
          type: 'tool_use',
          id: `gemini_call_${Date.now()}_${callIndex++}`,
          name: part.functionCall.name,
          input: part.functionCall.args || {},
          ...(part.thoughtSignature ? { thoughtSignature: part.thoughtSignature || '' } : {}),
        });
      } else if (part.text) {
        content.push({
          type: 'text',
          text: part.text,
          ...(part.thoughtSignature ? { thoughtSignature: part.thoughtSignature || '' } : {}),
        });
      }
    }

    return { content, usage, usedModelKey: modelKnown };
  }

  // GPT format: result[0].message with tool_calls[] or function_call
  if (Array.isArray(result) && result[0]?.message) {
    const msg = result[0].message;
    const content: any[] = [];

    if (msg.content) {
      content.push({ type: 'text', text: msg.content });
    }

    if (msg.tool_calls?.length) {
      for (const tc of msg.tool_calls) {
        const args = tc.function?.arguments;
        content.push({
          type: 'tool_use',
          id: tc.id,
          name: tc.function?.name,
          input: typeof args === 'string' ? JSON.parse(args) : args,
        });
      }
    } else if (msg.function_call) {
      const args = msg.function_call.arguments;
      content.push({
        type: 'tool_use',
        id: `gpt_call_${Date.now()}`,
        name: msg.function_call.name,
        input: typeof args === 'string' ? JSON.parse(args) : args,
      });
    }

    return { content, usage, usedModelKey: modelKnown };
  }

  // Fallback: assume Claude-like structure or return empty
  return { content: result?.content || [], usage, usedModelKey: modelKnown };
}

export function cleanHtml(html: string, config?: Config): string {
  return DOMPurify.sanitize(html, { ADD_ATTR: ['target'], ...config });
}

export function cleanQuillHtml(html: string, config?: Config): string {
  html = html
    ?.replace(/<p[^<]*?><\/p>/gm, '<p><br></p>')
    .replace(/&nbsp;/gm, ' ')
    .replace(/\xA0/gm, ' ')
    .trim();

  if (!html?.includes('<')) {
    return html;
  }

  return cleanHtml(cleanHtml(html, config), config);
}

export function b64EncodeUnicode(str: string): string {
  return btoa(
    encodeURIComponent(str).replace(/%([0-9A-F]{2})/g, function (match, p1) {
      return String.fromCharCode(parseInt(p1, 16));
    }),
  );
}

export function alphaNumericNormalize(str: string): string {
  return str
    ?.normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s/g, '-')
    .replace(/[^a-zA-Z0-9-]/g, '');
}
